/* eslint-disable react/sort-comp */
// File for logic corresponding to header component
import { Component } from 'js-component-framework';
import breakpoints from '@/client/src/js/utils/breakpoints';

/**
 * Component for site header
 */
export default class Header extends Component {
  /**
   * Start the component
   */
  constructor(config) {
    super(config);

    // Select all focus traps
    this.focusTraps = this.children.focusTraps;

    // Set up escape key listener function
    this.escapeListener = this.handleEscapeKey.bind(this);
    this.clickListener = this.handleOutsideClick.bind(this);
    this.closeSubMenus = this.closeSubMenus.bind(this);

    // If below lg breakpoint, apply mobile menu
    const desktopBreakpoint = breakpoints.lgMin;
    desktopBreakpoint.addEventListener('change', this.handleMediaQuery);
    this.handleMediaQuery(desktopBreakpoint);

    // Select all menu items with children, apply toggle
    const { menuParents } = this.children;
    if (menuParents.length) {
      this.initMenuParents(menuParents);
    }

    // Set target of first focus trap to last menu item link
    const { menuButtons } = this.children;
    const lastAnchor = menuButtons[(menuButtons.length - 1)];
    if (lastAnchor) {
      this.focusTraps[0].setAttribute('data-target', lastAnchor.getAttribute('id'));
    }

    // Apply focus trap behavior
    if (this.focusTraps.length) {
      this.initFocusTraps();
    }

    this.pageContainer = document.querySelector('#page');
  }

  handleMediaQuery = (desktopBreakpoint) => {
    if (!desktopBreakpoint.matches) {
      this.initMobileToggle();
    } else {
      this.removeMobileToggle();
    }
  }

  closeMenuToggle(toggle, overlayVisible) {
    toggle.setAttribute('aria-expanded', 'false');

    if (!overlayVisible) {
      this.pageContainer.classList.remove('submenu-is-open');
    }

    document.removeEventListener('click', this.clickListener);
  }

  openMenuToggle(toggle) {
    // Close any other open submenus
    const openToggle = document.querySelector('.menu-item-toggle[aria-expanded="true"]');
    if (openToggle) {
      const overlayVisible = (openToggle !== null);
      this.closeMenuToggle(openToggle, overlayVisible);
    }

    toggle.setAttribute('aria-expanded', 'true');
    this.pageContainer.classList.add('submenu-is-open');
  }

  static handleMenuToggle(toggle) {
    const currentState = toggle.getAttribute('aria-expanded');

    if (currentState === 'false') {
      this.openMenuToggle(toggle);
    } else {
      this.closeMenuToggle(toggle);
    }
  }

  handleOutsideClick(event) {
    if (!this.currentChild.contains(event.target)) {
      this.closeSubMenus();
    }
  }

  handleEscapeKey(event) {
    if (event.key === 'Escape') {
      this.closeMenu();
    }
  }

  /**
   * Applies AriaPopup module to this.children.target and this.children.controller
   */
  initMobileToggle() {
    const { open, close, dialog } = this.children;

    // Do nothing if there is no controller and target
    if (!open || !dialog) {
      return;
    }

    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', 'menu-heading');

    open.addEventListener('click', this.openMenu.bind(this));
    close.addEventListener('click', this.closeMenu.bind(this));
  }

  removeMobileToggle() {
    const { close, dialog } = this.children;

    // Do nothing if there is no close and dialog
    if (!close || !dialog) {
      return;
    }

    dialog.removeAttribute('role');
    dialog.removeAttribute('aria-modal');
    dialog.removeAttribute('aria-labelledby');
  }

  openMenu() {
    const { close, dialog } = this.children;

    dialog.classList.add('is-open');
    this.pageContainer.classList.add('menu-is-open');
    document.addEventListener('keyup', this.escapeListener);
    close.focus();
  }

  closeMenu() {
    const { open, dialog } = this.children;

    dialog.classList.remove('is-open');
    this.pageContainer.classList.remove('menu-is-open');
    document.removeEventListener('keyup', this.escapeListener);
    open.focus();
  }

  closeSubMenus() {
    // Close any toggled submenus
    const toggle = document.querySelector('.menu-item-toggle[aria-expanded="true"]');
    const searchStatus = document.querySelector('.site-header-search').classList.contains('is-open');

    if (toggle) {
      this.closeMenuToggle(toggle);

      if (!searchStatus) {
        toggle.focus();
      }
    }
  }

  initMenuParents(menuParents) {
    menuParents.forEach((child) => {
      const toggle = child.querySelector('.menu-item-toggle');

      toggle.addEventListener('click', () => {
        const currentState = toggle.getAttribute('aria-expanded');
        if (currentState === 'false') {
          this.openMenuToggle(toggle);
          this.currentChild = child;
          document.addEventListener('click', this.clickListener);
        } else {
          this.closeMenuToggle(toggle);
        }
      });
    });

    // Attach listener to element for escape key press, close all submenus on press
    document.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') {
        this.closeSubMenus();
      }
    });
  }

  initFocusTraps() {
    this.focusTraps.forEach((trap) => {
      const focusDestination = trap.getAttribute('data-target');

      if (focusDestination) {
        trap.addEventListener('focus', () => { document.getElementById(focusDestination).focus(); });
      }
    });
  }
}
